import { hubQueryKeys } from "@lifter/hub-ui/browser-shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { type AgentQueryOptions, useHubApi } from "./context";

const BindingRequirementSchema = z
  .object({ connectorType: z.string(), purpose: z.string() })
  .passthrough();
const BlueprintSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    description: z.string(),
    readme: z.string(),
    version: z.number(),
    bindingRequirements: z.array(BindingRequirementSchema),
    provenance: z.object({ sourceTitle: z.string(), packagedAt: z.string() }).passthrough(),
    createdAt: z.number(),
    updatedAt: z.number(),
  })
  .passthrough();
const BlueprintListSchema = z.object({ blueprints: z.array(BlueprintSchema) }).passthrough();
const BindingStatusSchema = BindingRequirementSchema.extend({
  status: z.enum(["ready", "missing"]),
  requiresConsent: z.literal(true),
  prompt: z.string(),
}).passthrough();
const InstantiationSchema = z
  .object({
    appId: z.string(),
    agentId: z.string(),
    title: z.string(),
    functional: z.boolean(),
    grantsCreated: z.literal(false),
    bindingRequirements: z.array(BindingStatusSchema),
  })
  .passthrough();

export type ApplicationBlueprint = z.infer<typeof BlueprintSchema>;
export type ApplicationBlueprintInstantiation = z.infer<typeof InstantiationSchema>;

const blueprintKeys = {
  all: ["application-blueprints"] as const,
  detail: (id: string) => ["application-blueprints", id] as const,
};

export function useApplicationBlueprintsQuery(options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: blueprintKeys.all,
    queryFn: async () =>
      BlueprintListSchema.parse(await api.request("/api/application-blueprints")),
    enabled: options.enabled ?? true,
  });
}

export function useApplicationBlueprintQuery(blueprintId: string) {
  const api = useHubApi();
  return useQuery({
    queryKey: blueprintKeys.detail(blueprintId),
    queryFn: async () =>
      BlueprintSchema.parse(
        await api.request(`/api/application-blueprints/${encodeURIComponent(blueprintId)}`),
      ),
    enabled: blueprintId.length > 0,
  });
}

export function useInstantiateApplicationBlueprintMutation(blueprintId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { agentId: string; title?: string }) =>
      InstantiationSchema.parse(
        await api.request(
          `/api/application-blueprints/${encodeURIComponent(blueprintId)}/instantiate`,
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(input),
          },
        ),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: hubQueryKeys.applications() }),
  });
}

export function usePackageApplicationBlueprintMutation(appId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      title: string;
      description: string;
      readme: string;
      bindingRequirements: Array<{ connectorType: string; purpose: string }>;
    }) =>
      BlueprintSchema.parse(
        await api.request(`/api/applications/internal/${encodeURIComponent(appId)}/blueprints`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(input),
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: blueprintKeys.all }),
  });
}
